import React, { useState } from "react";
import { useLanguage } from "@/i18n";

const themes = [
  { colors: ["#0ea5e9", "#1e293b"], active: true },
  { colors: ["#ec4899", "#1e293b"] },
  { colors: ["#22c55e", "#1e293b"] },
  { colors: ["#ef4444", "#1e293b"] },
  { colors: ["#3b82f6", "#1e293b"] },
  { colors: ["#a855f7", "#1e293b"] },
  { colors: ["#f97316", "#1e293b"] },
  { colors: ["rgba(255,255,255,0.2)", "#1e293b"] }
];

const ThemesSection = () => {
  const [activeTheme, setActiveTheme] = useState(0);
  const { t } = useLanguage();
  const themeNames = t('themes.themeNames');

  return (
    <section id="themes" className="py-20 sm:py-28 px-4 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/5 to-transparent pointer-events-none" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-16 space-y-4">
          <span className="text-primary text-sm font-medium uppercase tracking-widest">
            {t('themes.label')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
            {t('themes.title')}{" "}
            <span className="text-gradient">{t('themes.titleHighlight')}</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            {t('themes.description')}
          </p>
        </div>

        {/* Theme previews */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 sm:gap-4 mb-12">
          {themes.map((theme, index) => (
            <button
              key={index}
              onClick={() => setActiveTheme(index)}
              className={`group relative aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                activeTheme === index 
                  ? "border-primary scale-105 shadow-glow-sm" 
                  : "border-border hover:border-primary/50"
              }`}
            >
              {/* Theme preview */}
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: theme.colors[1] }}
              >
                {/* Accent color circle */}
                <div 
                  className="absolute bottom-2 right-2 w-4 h-4 rounded-full"
                  style={{ 
                    backgroundColor: theme.colors[0],
                    boxShadow: activeTheme === index ? `0 0 10px ${theme.colors[0]}` : 'none'
                  }}
                />
              </div>
              
              {/* Label on hover */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-[10px] text-white font-medium">{themeNames[index]}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Theme name display */}
        <div className="text-center">
          <p className="text-lg font-medium text-foreground">
            {t('themes.viewing')} <span className="text-primary">{themeNames[activeTheme]}</span>
          </p>
        </div>

        {/* Phone mockup with selected theme */}
        <div className="mt-12 flex justify-center">
          <div className="relative w-64 h-[500px] rounded-[2.5rem] bg-card border-4 border-border overflow-hidden shadow-2xl">
            {/* Phone notch */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-background rounded-b-xl z-10" />
            
            {/* Screen */}
            <div 
              className="absolute inset-2 rounded-[2rem] overflow-hidden transition-colors duration-500"
              style={{ backgroundColor: themes[activeTheme].colors[1] }}
            >
              {/* App content mockup */}
              <div className="p-4 pt-8">
                {/* Header */}
                <div className="flex items-center gap-2 mb-6">
                  <div 
                    className="w-8 h-8 rounded-lg transition-colors duration-500"
                    style={{ backgroundColor: themes[activeTheme].colors[0] }}
                  />
                  <div>
                    <div className="w-16 h-2 bg-white/20 rounded" />
                    <div className="w-10 h-1.5 bg-white/10 rounded mt-1" />
                  </div>
                </div>
                
                {/* Chat bubbles mockup */}
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <div 
                      className="w-3/4 h-10 rounded-2xl rounded-br-md transition-colors duration-500"
                      style={{ backgroundColor: themes[activeTheme].colors[0] }}
                    />
                  </div>
                  <div className="flex justify-start">
                    <div className="w-4/5 h-20 rounded-2xl rounded-bl-md bg-white/10" />
                  </div>
                  <div className="flex justify-end">
                    <div 
                      className="w-1/2 h-8 rounded-2xl rounded-br-md transition-colors duration-500"
                      style={{ backgroundColor: themes[activeTheme].colors[0] }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Input bar */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="flex gap-2">
                  <div className="flex-1 h-10 bg-white/10 rounded-full" />
                  <div 
                    className="w-10 h-10 rounded-full transition-colors duration-500"
                    style={{ backgroundColor: themes[activeTheme].colors[0] }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThemesSection;
